<template>
  <div class="col-12">
    <span class="item-detail-title py-2">{{ $t('contracts.duty_officer_schedule') }}</span>
    <contract-duty-officer
      ref="schedule"
      :users="users"
      :duty-officer-schedules="dutyOfficerSchedules"
      :selected-schedule="selectedSchedule"
      @save="saveSchedule"
      @delete-period="deletePeriod"
    />
  </div>
</template>

<script>
import contractDutyOfficer from "@/components/contract/ContractDatePicker.vue";
import {restApi} from "@/components/mixins/RestApiMixin";
import {inject} from "vue";

export default {
    components: {
        contractDutyOfficer
    },
    mixins: [restApi],
    props: {
        contractId: {
            type: Number,
            default: null
        },
        // { user, schedules } forwarded from the calendar when a user row is clicked
        selectedSchedule: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data() {
        return {
            dutyOfficerSchedules: [{}],
            users: [],
            loading: false,
        };
    },
    mounted() {
        this.fetchUsers()
    },
    methods: {
        updateCalendar() {
            this.$emit('updateCalendar');
        },
        // Reload the periods for whichever user is currently loaded in the form.
        // Called when the calendar changes a schedule (drag/resize) so the form stays in sync.
        refreshCurrentUser() {
            const current = this.$refs.schedule && this.$refs.schedule.user
            if (current && current.id != null) {
                this.reloadUserSchedules(current.id)
            }
        },
        saveSchedule(schedule) {
            // Split periods into new (no id -> create) and existing (id -> edit)
            const user = schedule.user
            const dates = schedule.dates || []
            const newDates = dates.filter(d => d.id == null)
            const existingDates = dates.filter(d => d.id != null)

            const requests = []
            if (newDates.length) {
                requests.push(this.createSchedules(user, newDates))
            }
            for (const period of existingDates) {
                requests.push(this.editSchedule(period))
            }

            if (!requests.length) {
                return
            }

            Promise.all(requests)
                .then(() => {
                    this.updateCalendar()
                    // Keep the same user selected and refresh their periods (with real ids)
                    this.reloadUserSchedules(user ? user.id : null)
                    this.showAlert(this.$t('common.alert_update_success'), 'success')
                })
                .catch(() => {
                    this.showAlert(this.$t('common.alert_update_failed'))
                })
        },
        // Re-fetch the selected user's schedules for this contract and refill the form,
        // so the user stays selected after saving (and new rows gain their real ids).
        reloadUserSchedules(userId) {
            if (!userId) {
                return
            }
            this.restFetch(this.dutyOfficerScheduleUrl + `?contract=${this.contractId}&user=${userId}`, response => {
                this.$refs.schedule.reloadPeriods(response.data || [])
            })
        },
        createSchedules(user, dates) {
            return new Promise((resolve, reject) => {
                const normalized = dates.map(d => ({
                    ...d,
                    start_date: this.toLocalNaiveString(d.start_date),
                    end_date: this.toLocalNaiveString(d.end_date),
                }))
                const params = { schedules: { user, dates: normalized } }
                this.restUpdate(this.dutyOfficerScheduleUrl + `?contract=${this.contractId}`, params, () => resolve(), () => reject())
            })
        },
        editSchedule(period) {
            return new Promise((resolve, reject) => {
                const params = {
                    id: period.id,
                    start_date: this.toLocalNaiveString(period.start_date),
                    end_date: this.toLocalNaiveString(period.end_date),
                }
                this.restUpdate(this.dutyOfficerScheduleEditUrl, params, () => resolve(), () => reject())
            })
        },
        // The DateTimePicker emits a UTC ISO string (e.g. 2026-09-30T05:00:00.000Z),
        // but the backend stores/returns datetimes without a timezone (e.g.
        // 2026-09-30T05:00:00), which the picker then reads back as LOCAL time -> a
        // timezone-sized shift. To keep what we save consistent with how it's read,
        // send the local wall-clock time as a zoneless string.
        toLocalNaiveString(value) {
            if (!value) {
                return value
            }
            const d = new Date(value)
            if (isNaN(d.getTime())) {
                return value
            }
            const pad = n => String(n).padStart(2, '0')
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        },
        deletePeriod(id) {
            this.restDelete(this.dutyOfficerScheduleUrl + `?id=${id}`, () => {
                this.updateCalendar()
                this.showAlert(this.$t('common.alert_update_success'), 'success')
            }, () => {
                this.showAlert(this.$t('common.alert_update_failed'))
            })
        },
        fetchUsers() {
            this.loading = true;
            this.restFetch(this.userUrl, response => {
                this.loading = false
                this.users = []
                const users = response.data
                users.forEach(user => {
                    this.users.push({
                        id: user.id,
                        first_name: user.first_name,
                        last_name: user.last_name,
                    })
                })
            })
        },
    }
}
</script>
